import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  InputAdornment,
  Divider,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import RateReviewIcon from "@mui/icons-material/RateReview";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CategoryIcon from "@mui/icons-material/Category";
import FilterListIcon from "@mui/icons-material/FilterList";
import LoginIcon from "@mui/icons-material/Login";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";

// TikTok Icon Component (MUI doesn't have TikTok icon)
const TikTokIcon = ({ fontSize = "48px" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ width: fontSize, height: fontSize }}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const HelpPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSection, setExpandedSection] = useState("getting-started");

  const documentation = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: <MapIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Welcome to the Christmas Lights Map! This website helps you discover and explore beautiful holiday light displays in your area.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Quick Start:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              Click <strong>"Explore the Map"</strong> on the home page to view all locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              Click on any marker to see location details
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              Use the category filter to find specific types of displays
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              Check out <strong>"Top Picks"</strong> to see the most popular locations
            </li>
          </Box>
        </Box>
      ),
    },
    {
      id: "exploring-map",
      title: "Exploring the Map",
      icon: <MapIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            The interactive map shows all Christmas light displays in your area. Each location is marked with a category-specific icon.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Map Features:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click on markers:</strong> Opens a detailed information card with photos, description, hours, and more
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Category icons:</strong> Different colored icons represent different categories (houses, events, markets, etc.)
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Zoom and pan:</strong> Use your mouse or touch gestures to navigate the map
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Search bar:</strong> Use the search bar at the top to find specific locations
            </li>
          </Box>
        </Box>
      ),
    },
    {
      id: "categories",
      title: "Categories & Filters",
      icon: <CategoryIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Filter locations by category or price to find exactly what you're looking for.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Available Categories:
          </Typography>
          <Grid container spacing={2} sx={{ marginBottom: "16px" }}>
            {["Houses", "Events", "Community Lights", "Markets", "Santa Visits", "Tree Farms", "Shows", "Ice Skating", "Dining", "Shopping", "Parades", "Charity"].map((cat) => (
              <Grid item key={cat}>
                <Chip
                  label={cat}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(196, 30, 58, 0.1)",
                    color: "#C41E3A",
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 500,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Filter Options:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>All:</strong> Shows all locations regardless of price
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Free:</strong> Shows only free locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Paid:</strong> Shows only paid locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Category dropdown:</strong> Select one or more categories to filter by type
            </li>
          </Box>
        </Box>
      ),
    },
    {
      id: "adding-locations",
      title: "Adding a Location",
      icon: <AddLocationIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Share your favorite Christmas light displays with the community by adding them to the map.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Steps to Add a Location:
          </Typography>
          <Box component="ol" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Log in:</strong> You need to be logged in to add locations. Click "Login" in the menu bar if you haven't already.
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Navigate to Add Location:</strong> Click "Add Location" in the menu bar or click the "Add Locations" card on the home page.
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Fill in the form:</strong>
              <Box component="ul" sx={{ paddingLeft: "24px", marginTop: "8px" }}>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Enter the location name</li>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Enter the address (use autocomplete for accuracy)</li>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Add a description</li>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Select the category</li>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Mark if it's free or paid</li>
                <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>Optionally add hours, website, and photos</li>
              </Box>
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Submit:</strong> Click "Add Location" to submit. Your location will appear on the map immediately!
            </li>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(196, 30, 58, 0.05)",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              borderLeft: "4px solid #C41E3A",
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "'Playfair Display', serif", color: "#666" }}>
              <strong>Note:</strong> All locations are reviewed to ensure accuracy and quality. Thank you for contributing to the community!
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "voting",
      title: "Voting for Locations",
      icon: <ThumbUpIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Show your appreciation for amazing displays by voting for your favorite locations.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            How to Vote:
          </Typography>
          <Box component="ol" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click on a location marker</strong> on the map to open the location details
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Log in</strong> if you haven't already (voting requires an account)
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click the "Vote" button</strong> in the location details card
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              Your vote is recorded! You can only vote once per location.
            </li>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(196, 30, 58, 0.05)",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              borderLeft: "4px solid #C41E3A",
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "'Playfair Display', serif", color: "#666" }}>
              <strong>Tip:</strong> Locations with more votes appear higher in the "Top Picks" page, helping others discover the best displays!
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "reviews",
      title: "Leaving Reviews",
      icon: <RateReviewIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Share your experience and help others discover great displays by leaving reviews.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            How to Leave a Review:
          </Typography>
          <Box component="ol" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click on a location marker</strong> to open the location details
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Log in</strong> if you haven't already (reviews require an account)
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Scroll to the "Add a Review" section</strong> at the bottom of the location card
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Type your review</strong> in the text field
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click "Submit Review"</strong> to share your thoughts with the community
            </li>
          </Box>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Viewing Reviews:
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8, color: "#555" }}>
            All reviews are visible in the location details card. You can read what others have said about each display to help plan your visit.
          </Typography>
        </Box>
      ),
    },
    {
      id: "top-picks",
      title: "Top Picks",
      icon: <EmojiEventsIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            Discover the most popular Christmas light displays based on community votes.
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Features:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Top 10 locations:</strong> See the most voted locations in one place
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Category filters:</strong> Filter by category to see top picks in each type
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Search:</strong> Search for specific locations by name or address
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Click to view:</strong> Click on any location card to see full details
            </li>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8, color: "#555" }}>
            Access Top Picks from the menu bar or by clicking "Top Picks" on the home page.
          </Typography>
        </Box>
      ),
    },
    {
      id: "account",
      title: "Creating an Account",
      icon: <LoginIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            You need an account to add locations, vote, and leave reviews. Creating an account is free and easy!
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Sign Up Options:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Email & Password:</strong> Create an account with your email address and a password. You'll need to verify your email address.
            </li>
            <li style={{ marginBottom: "12px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Google Sign-In:</strong> Sign in quickly with your Google account (one-click sign-in).
            </li>
          </Box>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Email Verification:
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8, color: "#555" }}>
            After signing up with email, check your inbox (and spam folder) for a verification email. Click the link in the email to verify your account. 
            You can resend the verification email if needed.
          </Typography>
        </Box>
      ),
    },
    {
      id: "mobile-app",
      title: "Mobile App Coming Soon",
      icon: <LocationOnIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography variant="body1" sx={{ marginBottom: "16px", fontFamily: "'Playfair Display', serif", lineHeight: 1.8 }}>
            We're working on a mobile app that will enhance your Christmas lights exploration experience!
          </Typography>
          <Typography variant="h6" sx={{ marginTop: "20px", marginBottom: "12px", fontFamily: "'Playfair Display', serif", fontWeight: 600, color: "#333" }}>
            Coming Features:
          </Typography>
          <Box component="ul" sx={{ paddingLeft: "24px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Offline maps:</strong> Download maps for offline use
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Route planning:</strong> Plan optimized routes to visit multiple locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Push notifications:</strong> Get notified about new locations and events
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Enhanced navigation:</strong> Turn-by-turn directions to each location
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              <strong>Saved favorites:</strong> Save your favorite locations for easy access
            </li>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(196, 30, 58, 0.05)",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              borderLeft: "4px solid #C41E3A",
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Stay tuned for the app launch! In the meantime, you can use the website on your mobile browser for a great experience.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "privacy-policy",
      title: "Privacy Policy",
      icon: <PrivacyTipIcon sx={{ fontSize: "28px", color: "#C41E3A" }} />,
      content: (
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, fontStyle: "italic" }}
          >
            Last Updated: December 7, 2025
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 2,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Introduction
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            Christmas Lights Map ("we," "our," or "us") is committed to
            protecting your privacy. This Privacy Policy explains how we
            collect, use, and safeguard your information when you use our mobile
            application and website.
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 3,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Information We Collect
          </Typography>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Location Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            We collect your location data (with your permission) to show nearby
            Christmas light displays and help you navigate to locations.
            Location data is only collected when you grant permission and is
            used solely for app functionality.
          </Typography>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            User-Generated Content
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            When you add a location, we collect the information you provide,
            including:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Location name and address
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Photos you upload
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Descriptions and reviews
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Your votes and ratings
            </li>
          </Box>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Account Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            If you choose to sign in with Google, we collect your email address
            and display name. You can use the app without creating an account.
          </Typography>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Usage Data
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            We collect anonymous usage statistics to improve the app, including
            features you use, app crashes and errors, and general app
            performance metrics.
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 3,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            How We Use Your Information
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 3 }}>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              To provide and improve our services
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              To display locations on the map
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              To allow you to add, vote on, and review locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              To help you navigate to locations
            </li>
            <li style={{ marginBottom: "8px", fontFamily: "'Playfair Display', serif", color: "#555" }}>
              To analyze app usage and improve user experience
            </li>
          </Box>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 3,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Third-Party Services
          </Typography>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Google Maps
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            We use Google Maps to display locations and provide navigation.{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              Google's privacy policy
            </a>
          </Typography>

          <Typography
            variant="body2"
            component="h4"
            sx={{
              mt: 2,
              mb: 1,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Google Mobile Ads
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            We display advertisements in the app.{" "}
            <a
              href="https://policies.google.com/technologies/ads"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              Google's advertising privacy policy
            </a>
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 3,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Your Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            You have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Access your personal data
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Delete your account and associated data
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Opt out of location tracking (disable location permissions)
            </li>
            <li style={{ marginBottom: "6px", fontFamily: "'Playfair Display', serif", color: "#666" }}>
              Request a copy of your data
            </li>
          </Box>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            To exercise these rights, contact us at:{" "}
            <a
              href="mailto:prabhjotlikesinnovation@gmail.com"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              prabhjotlikesinnovation@gmail.com
            </a>
            {", "}
            <a
              href="https://www.instagram.com/christmaslightsmap"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              @christmaslightsmap
            </a>
            {", "}
            <a
              href="https://www.facebook.com/profile.php?id=61584943377114"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              Facebook
            </a>
            {", or "}
            <a
              href="https://www.tiktok.com/@christmaslightsmap?is_from_webapp=1&sender_device=pc"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              TikTok
            </a>
            .
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              mt: 3,
              mb: 1.5,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#333",
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              lineHeight: 1.8,
              color: "#555",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            If you have questions about this Privacy Policy, please contact us
            at:{" "}
            <a
              href="mailto:prabhjotlikesinnovation@gmail.com"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              prabhjotlikesinnovation@gmail.com
            </a>
            {", "}
            <a
              href="https://www.instagram.com/christmaslightsmap"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              @christmaslightsmap
            </a>
            {", "}
            <a
              href="https://www.facebook.com/profile.php?id=61584943377114"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              Facebook
            </a>
            {", or "}
            <a
              href="https://www.tiktok.com/@christmaslightsmap?is_from_webapp=1&sender_device=pc"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C41E3A", textDecoration: "none" }}
            >
              TikTok
            </a>
            .
          </Typography>
        </Box>
      ),
    },
  ];

  const handleSectionChange = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? "" : sectionId);
  };

  const filteredDocumentation = documentation.filter((doc) =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.content.props.children.props.children
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FAFAFA",
        paddingTop: { xs: "100px", sm: "120px" },
        paddingBottom: "60px",
      }}
    >
      <Box sx={{ maxWidth: "1000px", margin: "0 auto", padding: { xs: "20px", sm: "40px" } }}>
        {/* Header */}
        <Box sx={{ marginBottom: "40px", textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: 700,
              color: "#C41E3A",
              marginBottom: "12px",
            }}
          >
            Help & Support
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Playfair Display', serif",
              color: "#666",
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
            }}
          >
            Everything you need to know about using the Christmas Lights Map
          </Typography>
        </Box>

        {/* Search Bar */}
        <Card
          sx={{
            marginBottom: "32px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <CardContent sx={{ padding: "20px" }}>
        <TextField
          fullWidth
              placeholder="Search help topics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#C41E3A" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C41E3A",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C41E3A",
                  },
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Documentation Sections */}
        {filteredDocumentation.length === 0 ? (
          <Card
            sx={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              padding: "40px",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#999",
              }}
            >
              No topics found. Try searching for something else.
            </Typography>
          </Card>
        ) : (
          <Box>
            {filteredDocumentation.map((doc) => (
              <Accordion
                key={doc.id}
                expanded={expandedSection === doc.id}
                onChange={() => handleSectionChange(doc.id)}
                sx={{
                  marginBottom: "16px",
                  borderRadius: "12px !important",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  "&:before": {
                    display: "none",
                  },
                  "&.Mui-expanded": {
                    marginBottom: "16px",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#C41E3A" }} />}
                  sx={{
                    padding: "20px 24px",
                    "&:hover": {
                      backgroundColor: "rgba(196, 30, 58, 0.02)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "16px", width: "100%" }}>
                    {doc.icon}
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 600,
                        color: "#333",
                        flex: 1,
                      }}
                    >
                      {doc.title}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "24px" }}>{doc.content}</AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Support Section */}
        <Card
          sx={{
            marginTop: "40px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            background: "linear-gradient(135deg, rgba(196, 30, 58, 0.05) 0%, rgba(196, 30, 58, 0.1) 100%)",
          }}
        >
          <CardContent sx={{ padding: "32px", textAlign: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "center", gap: "24px", marginBottom: "16px" }}>
              <IconButton
                component="a"
                href="mailto:prabhjotlikesinnovation@gmail.com"
                sx={{
                  color: "#C41E3A",
                  "&:hover": {
                    backgroundColor: "rgba(196, 30, 58, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <EmailIcon sx={{ fontSize: "48px" }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.instagram.com/christmaslightsmap"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#C41E3A",
                  "&:hover": {
                    backgroundColor: "rgba(196, 30, 58, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <InstagramIcon sx={{ fontSize: "48px" }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.facebook.com/profile.php?id=61584943377114"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#C41E3A",
                  "&:hover": {
                    backgroundColor: "rgba(196, 30, 58, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <FacebookIcon sx={{ fontSize: "48px" }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.tiktok.com/@christmaslightsmap?is_from_webapp=1&sender_device=pc"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#C41E3A",
                  "&:hover": {
                    backgroundColor: "rgba(196, 30, 58, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <TikTokIcon />
              </IconButton>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                color: "#333",
                marginBottom: "12px",
              }}
            >
              Still Need Help?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#666",
                marginBottom: "24px",
              }}
            >
              Can't find what you're looking for? We're here to help!
            </Typography>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "16px", justifyContent: "center", marginBottom: "16px" }}>
              <Button
                variant="contained"
                href="mailto:prabhjotlikesinnovation@gmail.com"
                startIcon={<EmailIcon />}
                sx={{
                  backgroundColor: "#C41E3A",
                  color: "#FFFFFF",
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 600,
                  padding: "12px 32px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#DC143C",
                  },
                }}
              >
                Contact Support
              </Button>
              <Button
                variant="outlined"
                href="https://www.instagram.com/christmaslightsmap"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<InstagramIcon />}
                sx={{
                  borderColor: "#C41E3A",
                  color: "#C41E3A",
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 600,
                  padding: "12px 32px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#DC143C",
                    backgroundColor: "rgba(196, 30, 58, 0.05)",
                  },
                }}
              >
                Instagram
              </Button>
              <Button
                variant="outlined"
                href="https://www.facebook.com/profile.php?id=61584943377114"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<FacebookIcon />}
                sx={{
                  borderColor: "#C41E3A",
                  color: "#C41E3A",
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 600,
                  padding: "12px 32px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#DC143C",
                    backgroundColor: "rgba(196, 30, 58, 0.05)",
                  },
                }}
              >
                Facebook
              </Button>
              <Button
                variant="outlined"
                href="https://www.tiktok.com/@christmaslightsmap?is_from_webapp=1&sender_device=pc"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<TikTokIcon sx={{ fontSize: "24px" }} />}
                sx={{
                  borderColor: "#C41E3A",
                  color: "#C41E3A",
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 600,
                  padding: "12px 32px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#DC143C",
                    backgroundColor: "rgba(196, 30, 58, 0.05)",
                  },
                }}
              >
                TikTok
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "16px", justifyContent: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  color: "#999",
                }}
              >
                prabhjotlikesinnovation@gmail.com
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  color: "#999",
                }}
              >
                @christmaslightsmap
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default HelpPage;
